import { API_BASE_URL } from '../config/env';
import type { AuthTokens } from '../types/auth';
import { formatApiErrorMessage } from '../utils/apiErrorMessage';
import { AuthApiError } from './AuthApiError';
import {
  clearStoredTokens,
  getStoredRefreshToken,
  setStoredTokens,
} from './authStorage';
import { parseAuthTokensPayload, parseExpiresIn } from './parseTokens';

let inflightRefresh: Promise<AuthTokens> | null = null;
let onTokensStored: (() => void) | null = null;
let onSessionCleared: (() => void) | null = null;

export function setOnTokensStored(handler: (() => void) | null): void {
  onTokensStored = handler;
}

export function setOnSessionCleared(handler: (() => void) | null): void {
  onSessionCleared = handler;
}

async function readRefreshJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function storeTokensFromAuthResponse(data: unknown, tokens: AuthTokens): void {
  setStoredTokens(tokens.accessToken, tokens.refreshToken, parseExpiresIn(data));
  onTokensStored?.();
}

async function performRefresh(refreshToken: string): Promise<AuthTokens> {
  const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
    credentials: 'same-origin',
  });
  const body = await readRefreshJson(res);

  if (!res.ok) {
    clearStoredTokens();
    onSessionCleared?.();
    const msg = formatApiErrorMessage(body, res.statusText) || 'Session expired';
    throw new AuthApiError(msg, res.status, body);
  }

  const tokens = parseAuthTokensPayload(body);
  if (!tokens) {
    clearStoredTokens();
    throw new AuthApiError('Invalid refresh response: missing tokens', 500, body);
  }
  storeTokensFromAuthResponse(body, tokens);
  return tokens;
}

/** Deduplicated refresh — concurrent 401s share one in-flight `POST /auth/refresh`. */
export async function refreshSession(): Promise<AuthTokens> {
  const rt = getStoredRefreshToken();
  if (!rt) {
    clearStoredTokens();
    throw new AuthApiError('No refresh token', 401);
  }
  if (!inflightRefresh) {
    inflightRefresh = performRefresh(rt).finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}
